import { PublicConfigurationT } from "@swo/shared";
import { EasySizeConfig, EasySizeConfiguration, EasySizeProduct } from "./types";
import { AppStoreState } from "../../../store/app-store/app-store-ctx-provider";
import { VariantPickerVariation_ClothSize } from "../PDP_SingleImageCarousel/pickers/utils";
import { withKey } from "../../../utils/js/hash";
import { PDPSwarovskiProductFrontend } from "../PDP_SingleImageCarousel/state/types";

export type ESProductConfig = {
  widget: EasySizeConfigurationCallbackless;
  config: EasySizeConfig;
  key: number;
};

export function getEsConfiguration(
  state: AppStoreState,
  selectedProduct: PDPSwarovskiProductFrontend,
  attributes: Array<Omit<VariantPickerVariation_ClothSize, "tag">>,
  context: {
    countryIsoCode: string;
    languageName: string;
    userId: string | undefined;
  },
): ESProductConfig | undefined {
  const { easySize } = state.config.integrations;
  if (!easySize || !selectedProduct) {
    return;
  }

  const shopId = getShopId(easySize, context.countryIsoCode);
  if (!shopId) {
    return;
  }

  const variantsES = selectedProduct.variants.map(v => v.integrations?.easySize);
  if (!variantsES.every(es => (es as Record<string, unknown>)?.enabled)) {
    return;
  }

  const gender = ((variantsES[0] as Record<string, unknown>)?.gender as any)?.key;

  const product = {
    id: selectedProduct.id,
    modelName: selectedProduct.key,
    gender: gender,
    clothingType: selectedProduct.categories[0]!.current.key,
    imageUrl: selectedProduct.images.thumbnail?.url || "",
    // We'd like to use `value` instead of `label` here but EasySize doesn't currently make a distinction here
    sizesInStock: attributes.flatMap(v => (v.state !== "disabled" ? [v.size.label] : [])),
  };
  const config = {
    shopId: shopId,
    src: easySize.src,
    integrity: easySize.integrity,
  };

  const widget = getWidget(product, {
    shopId: shopId,
    languageName: context.languageName,
    userId: context.userId,
  });

  const { key, obj } = withKey({ config, widget });

  return {
    ...obj,
    key,
  };
}

function getShopId(
  easySize: NonNullable<PublicConfigurationT["integrations"]["easySize"]>,
  countryIsoCode: string,
): string | undefined {
  if (countryIsoCode.toUpperCase() === "US") {
    return easySize.shopId.US;
  } else {
    return easySize.shopId.NON_US;
  }
}

export type EasySizeConfigurationCallbackless = Omit<EasySizeConfiguration, "loaded" | "size_update_function">;

function getWidget(
  product: EasySizeProduct,
  opts: { languageName: string; shopId: string; userId: string | undefined },
) {
  const placeholders = {
    main: "#es-product_size" as const,
    recommendation: "#es-product_size" as const,
    addToCart: "#es-placeholder-3" as const,
    sizeSelector: "#es-placeholer-4" as const,
  };

  const configuration: EasySizeConfigurationCallbackless = {
    product_id: product.id,
    product_type: product.clothingType,
    product_model: product.modelName,
    image_url: product.imageUrl,
    product_brand: "Swarovski Optik",
    product_gender: product.gender,
    product_sku: undefined,
    product_fit: undefined,
    language: opts.languageName,
    sizes_in_stock: Object.fromEntries(product.sizesInStock.map(size => [size, 1])),
    shop_id: opts.shopId,
    user_id: opts.userId || "-1",
    placeholder: placeholders.main,
    recommendation_placeholder: placeholders.recommendation,
    add_to_cart_btn_selector: placeholders.addToCart,
    size_selector: placeholders.sizeSelector,
    cookies_enabled: true,
    dom_updates: false,
  };

  return configuration;
}
